import React, { Component } from 'react'
import {
	EyeIcon,
	UsersIcon,
	RefreshIcon,
	TrendingUpIcon,
	TrendingDownIcon,
	DeviceMobileIcon } from '@heroicons/react/outline'
import InfoTip from './InfoTip'

// Espaces fins insécables comme séparateurs de milliers (convention FR)
function fmt(n)
{
	if (n === null || n === undefined)
		return '0'
	return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

const PERIOD_LABELS = {
	7: 'Sur 7 jours',
	30: 'Sur 30 jours',
	90: 'Sur 90 jours',
	0: 'Depuis le lancement'
}

const TONES = {
	indigo: { tile: 'bg-indigo-50', icon: 'text-indigo-600' },
	blue: { tile: 'bg-blue-50', icon: 'text-blue-600' },
	emerald: { tile: 'bg-emerald-50', icon: 'text-emerald-600' },
	slate: { tile: 'bg-slate-100', icon: 'text-slate-500' }
}

class StatsKpis extends Component {
	_deltaPill = (delta) => {
		if (delta === null || delta === undefined)
			return null
		const pct = Math.round(delta * 100)
		const up = pct >= 0
		return (
			<span className={'inline-flex items-center gap-0.5 text-2xs font-black px-2 py-0.5 rounded-full ' + (up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')}>
				{up
					? <TrendingUpIcon className="w-3 h-3" aria-hidden="true" />
					: <TrendingDownIcon className="w-3 h-3" aria-hidden="true" />}
				{(up ? '+' : '') + pct}%
			</span>
		)
	}

	_renderCard = (card) => {
		const tone = TONES[card.tone] || TONES.slate
		const Icon = card.icon
		return (
			<div key={card.label} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
				<div className="flex items-center justify-between">
					<span className="inline-flex items-center gap-1">
						<span className="text-2xs font-bold text-slate-400 uppercase tracking-widest">{card.label}</span>
						{card.tip && <InfoTip text={card.tip} />}
					</span>
					<div className={'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ' + tone.tile}>
						<Icon className={'w-5 h-5 ' + tone.icon} aria-hidden="true" />
					</div>
				</div>
				<div className="flex items-center gap-2 mt-2">
					<p className="text-3xl font-black text-slate-900 my-0 leading-none">{card.value}</p>
					{this._deltaPill(card.delta)}
				</div>
				{card.sub &&
					<p className="mt-2 mb-0 text-xs font-semibold text-slate-400 truncate">{card.sub}</p>
				}
			</div>
		)
	}

	render() {
		if (this.props.loading === true)
		{
			return (
				<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
					{[1, 2, 3, 4].map(key => (
						<div key={key} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 animate-pulse">
							<div className="flex items-center justify-between mb-4">
								<div className="h-3 bg-slate-100 rounded-full w-2/3"></div>
								<div className="w-9 h-9 bg-slate-100 rounded-xl"></div>
							</div>
							<div className="h-8 bg-slate-200 rounded-lg w-1/2"></div>
						</div>
					))}
				</div>
			)
		}
		if (this.props.error === true || !this.props.kpis)
		{
			return (
				<div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
					<h2 className="text-2xs font-black text-slate-400 uppercase tracking-widest mt-0 mb-2">Vue d'ensemble</h2>
					<p className="text-sm font-medium text-slate-500 mt-0 mb-3">Données indisponibles.</p>
					<button
						type="button"
						onClick={this.props.onRetry}
						className="inline-flex items-center gap-1.5 px-4 py-2 text-2xs font-black uppercase tracking-widest rounded-xl text-indigo-700 cursor-pointer transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-500"
						style={{ backgroundImage: 'none', backgroundColor: '#eef2ff' }}
					>
						<RefreshIcon className="w-4 h-4" aria-hidden="true" />
						Réessayer
					</button>
				</div>
			)
		}
		const k = this.props.kpis
		const periodLabel = PERIOD_LABELS[this.props.days] || 'Sur la période'
		const cards = [
			{
				icon: EyeIcon,
				tone: 'indigo',
				label: 'Pages vues',
				value: fmt(k.visits),
				delta: k.visitsDelta,
				sub: periodLabel,
				tip: 'Nombre d\'écrans ouverts par vos campeurs sur la période. Chaque page consultée compte pour une.'
			},
			{
				icon: UsersIcon,
				tone: 'blue',
				label: 'Visiteurs',
				value: fmt(k.visitors),
				delta: k.visitorsDelta,
				sub: 'Appareils distincts',
				tip: 'Nombre d\'appareils différents ayant ouvert l\'app. Un même campeur sur deux appareils compte pour deux.'
			},
			{
				icon: RefreshIcon,
				tone: 'emerald',
				label: 'Pages vues / visiteur',
				value: fmt(k.visitsPerVisitor),
				delta: k.visitsPerVisitorDelta,
				sub: 'Engagement moyen',
				tip: 'En moyenne, combien d\'écrans chaque visiteur a consultés. Plus c\'est haut, plus l\'app est explorée.'
			},
			{
				icon: DeviceMobileIcon,
				tone: 'slate',
				label: 'App installée',
				value: fmt(k.installsPct) + '%',
				delta: k.installsDelta,
				sub: fmt(k.installs) + ' visiteur' + ((k.installs > 1) ? 's' : '') + ' via l\'app',
				tip: 'Part des visiteurs qui utilisent l\'app installée sur leur écran d\'accueil, plutôt que le simple lien web.'
			}
		]
		return (
			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				{cards.map(card => this._renderCard(card))}
			</div>
		)
	}
}

export default StatsKpis
